/**
 * GODMODE Sound Entrainment Layer - Web Audio Implementation
 * Real-time audio processing engine for entrainment modulation
 * 
 * DSP Signal Chain:
 * 1. Input trim
 * 2. Analysis (RMS, tempo, spectral)
 * 3. Entrainment lanes (Pulse, Binaural, Spectral Embed)
 * 4. Mix-safe limiter
 * 5. Output trim + safety cap
 */

// Default preset configurations
export const PRESETS = {
  CALM: {
    name: "CALM",
    description: "Gentle relaxation with subtle entrainment",
    pulse: { enabled: true, freq_hz: 0.15, depth: 0.08, shape: "raisedCosine" },
    binaural: { enabled: false, carrier_hz: 160, beat_hz: 6, level_db: -36 },
    embed: { enabled: true, source: "pinkNoise", amount: 0.15, duck_strength: 0.6 }
  },
  FOCUS: {
    name: "FOCUS",
    description: "Enhanced concentration with beta entrainment",
    pulse: { enabled: true, freq_hz: 0.25, depth: 0.12, shape: "raisedCosine" },
    binaural: { enabled: true, carrier_hz: 200, beat_hz: 14, level_db: -30 },
    embed: { enabled: true, source: "pinkNoise", amount: 0.1, duck_strength: 0.7 }
  },
  SLEEP: {
    name: "SLEEP",
    description: "Deep relaxation with delta wave progression",
    pulse: { enabled: true, freq_hz: 0.08, depth: 0.06, shape: "raisedCosine" },
    binaural: { enabled: true, carrier_hz: 120, beat_hz: 2, level_db: -36 },
    embed: { enabled: true, source: "pinkNoise", amount: 0.2, duck_strength: 0.5 },
    timeline: [
      { t_sec: 0, beat_hz: 8 },
      { t_sec: 1200, beat_hz: 4 },
      { t_sec: 2400, beat_hz: 2 }
    ]
  },
  PEAK: {
    name: "PEAK",
    description: "High-energy gamma activation",
    pulse: { enabled: true, freq_hz: 0.5, depth: 0.18, shape: "trapezoidSoft" },
    binaural: { enabled: true, carrier_hz: 300, beat_hz: 40, level_db: -24 },
    embed: { enabled: true, source: "sineBank", amount: 0.08, duck_strength: 0.8 }
  }
};

// Safety constraints
const SAFETY = {
  maxOutputCeiling_db: -1.0,
  maxPulseDepth: 0.25,
  maxBinauralLevel_db: -18,
  smoothingTime_ms: 50,
  dcBlockFreq_hz: 20
};

// Convert dB to linear gain
const dbToGain = (db) => Math.pow(10, db / 20);
const gainToDb = (gain) => 20 * Math.log10(Math.max(gain, 0.0001));

/**
 * Entrainment Engine Class
 * Web Audio API implementation of the GODMODE Entrainment Layer
 */
export class EntrainmentEngine {
  constructor() {
    this.audioContext = null;
    this.isInitialized = false;
    this.isProcessing = false;
    this.currentMode = "CALM";
    
    // Analysis state
    this.analysis = {
      rms: 0,
      peak: 0,
      tempo: 120,
      spectralCentroid: 0,
      limiterGR: 0
    };
    
    // Parameters with defaults
    this.params = {
      inputTrim_db: 0,
      outputTrim_db: 0,
      maxCeiling_db: -1,
      hqMode: false,
      headphoneMode: false,
      
      pulse: {
        enabled: true,
        freq_hz: 0.15,
        depth: 0.08,
        shape: "raisedCosine",
        syncToBpm: false,
        phase: 0
      },
      
      binaural: {
        enabled: false,
        carrier_hz: 160,
        beat_hz: 6,
        level_db: -36,
        fade_ms: 200
      },
      
      embed: {
        enabled: true,
        source: "pinkNoise",
        amount: 0.15,
        duck_strength: 0.6,
        bands: [
          { enabled: true, low_hz: 80, high_hz: 160, gain_db: -24, q: 0.8 },
          { enabled: true, low_hz: 300, high_hz: 600, gain_db: -26, q: 0.9 },
          { enabled: false, low_hz: 2000, high_hz: 4000, gain_db: -28, q: 1.0 }
        ]
      },
      
      osc: {
        enabled: false,
        host: "127.0.0.1",
        port: 9000,
        rate_hz: 30
      }
    };
    
    // Audio nodes
    this.nodes = {};
    
    // Callbacks
    this.onAnalysisUpdate = null;
    this.onStateChange = null;
  }
  
  /**
   * Initialize the audio engine
   */
  async init() {
    if (this.isInitialized) return;
    
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: 48000,
        latencyHint: "interactive"
      });
      
      await this._buildAudioGraph();
      this.isInitialized = true;
      
      // Start analysis loop
      this._startAnalysisLoop();
      
      console.log("[EntrainmentEngine] Initialized successfully");
      return true;
    } catch (err) {
      console.error("[EntrainmentEngine] Init failed:", err);
      return false;
    }
  }
  
  /**
   * Build the complete audio processing graph
   */
  async _buildAudioGraph() {
    const ctx = this.audioContext;
    
    // Input/Output nodes
    this.nodes.inputGain = ctx.createGain();
    this.nodes.outputGain = ctx.createGain();
    this.nodes.masterLimiter = ctx.createDynamicsCompressor();
    
    // Analysis node
    this.nodes.analyser = ctx.createAnalyser();
    this.nodes.analyser.fftSize = 2048;
    this.nodes.analyser.smoothingTimeConstant = 0.8;
    
    // DC blocking high-pass filter
    this.nodes.dcBlock = ctx.createBiquadFilter();
    this.nodes.dcBlock.type = "highpass";
    this.nodes.dcBlock.frequency.value = SAFETY.dcBlockFreq_hz;
    this.nodes.dcBlock.Q.value = 0.707;
    
    // Pulse modulator (using gain node with LFO)
    this.nodes.pulseGain = ctx.createGain();
    this.nodes.pulseLFO = ctx.createOscillator();
    this.nodes.pulseLFOGain = ctx.createGain();
    this.nodes.pulseLFO.type = "sine";
    this.nodes.pulseLFO.frequency.value = this.params.pulse.freq_hz;
    this.nodes.pulseLFOGain.gain.value = this.params.pulse.depth;
    
    // Binaural oscillators
    this.nodes.binauralLeft = ctx.createOscillator();
    this.nodes.binauralRight = ctx.createOscillator();
    this.nodes.binauralGainL = ctx.createGain();
    this.nodes.binauralGainR = ctx.createGain();
    this.nodes.binauralMerger = ctx.createChannelMerger(2);
    
    const binauralLevel = dbToGain(this.params.binaural.level_db);
    this.nodes.binauralLeft.frequency.value = this.params.binaural.carrier_hz - this.params.binaural.beat_hz / 2;
    this.nodes.binauralRight.frequency.value = this.params.binaural.carrier_hz + this.params.binaural.beat_hz / 2;
    this.nodes.binauralGainL.gain.value = this.params.binaural.enabled ? binauralLevel : 0;
    this.nodes.binauralGainR.gain.value = this.params.binaural.enabled ? binauralLevel : 0;
    
    // Spectral embed (pink noise source)
    this.nodes.noiseSource = this._createPinkNoiseNode();
    this.nodes.embedGain = ctx.createGain();
    this.nodes.embedGain.gain.value = this.params.embed.amount;
    
    // Embed bandpass filters
    this.nodes.embedFilters = this.params.embed.bands.map((band, i) => {
      const filter = ctx.createBiquadFilter();
      filter.type = "bandpass";
      filter.frequency.value = Math.sqrt(band.low_hz * band.high_hz);
      filter.Q.value = band.q;
      return filter;
    });
    
    // Configure limiter
    this.nodes.masterLimiter.threshold.value = SAFETY.maxOutputCeiling_db;
    this.nodes.masterLimiter.knee.value = 3;
    this.nodes.masterLimiter.ratio.value = 20;
    this.nodes.masterLimiter.attack.value = 0.001;
    this.nodes.masterLimiter.release.value = 0.1;
    
    // Connect the graph
    this._connectGraph();
    
    // Start oscillators
    this.nodes.pulseLFO.start();
    this.nodes.binauralLeft.start();
    this.nodes.binauralRight.start();
  }
  
  /**
   * Connect all audio nodes in the signal chain
   */
  _connectGraph() {
    const ctx = this.audioContext;
    
    // Main signal path: input -> analysis -> pulse -> limiter -> output
    this.nodes.inputGain.connect(this.nodes.analyser);
    this.nodes.inputGain.connect(this.nodes.pulseGain);
    
    // Pulse LFO modulation
    this.nodes.pulseLFO.connect(this.nodes.pulseLFOGain);
    this.nodes.pulseLFOGain.connect(this.nodes.pulseGain.gain);
    
    // Pulse -> DC Block -> Limiter
    this.nodes.pulseGain.connect(this.nodes.dcBlock);
    this.nodes.dcBlock.connect(this.nodes.masterLimiter);
    
    // Binaural (stereo)
    this.nodes.binauralLeft.connect(this.nodes.binauralGainL);
    this.nodes.binauralRight.connect(this.nodes.binauralGainR);
    this.nodes.binauralGainL.connect(this.nodes.binauralMerger, 0, 0);
    this.nodes.binauralGainR.connect(this.nodes.binauralMerger, 0, 1);
    this.nodes.binauralMerger.connect(this.nodes.masterLimiter);
    
    // Embed signal path
    this.nodes.noiseSource.connect(this.nodes.embedGain);
    this.nodes.embedFilters.forEach(filter => {
      this.nodes.embedGain.connect(filter);
      filter.connect(this.nodes.masterLimiter);
    });
    
    // Final output
    this.nodes.masterLimiter.connect(this.nodes.outputGain);
    this.nodes.outputGain.connect(ctx.destination);
  }
  
  /**
   * Create pink noise generator
   */
  _createPinkNoiseNode() {
    const ctx = this.audioContext;
    const bufferSize = 2 * ctx.sampleRate;
    const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const output = noiseBuffer.getChannelData(0);
    
    // Pink noise using Voss-McCartney algorithm
    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
    for (let i = 0; i < bufferSize; i++) {
      const white = Math.random() * 2 - 1;
      b0 = 0.99886 * b0 + white * 0.0555179;
      b1 = 0.99332 * b1 + white * 0.0750759;
      b2 = 0.96900 * b2 + white * 0.1538520;
      b3 = 0.86650 * b3 + white * 0.3104856;
      b4 = 0.55000 * b4 + white * 0.5329522;
      b5 = -0.7616 * b5 - white * 0.0168980;
      output[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
      b6 = white * 0.115926;
    }
    
    const noiseSource = ctx.createBufferSource();
    noiseSource.buffer = noiseBuffer;
    noiseSource.loop = true;
    noiseSource.start();
    
    return noiseSource;
  }
  
  /**
   * Start the analysis update loop
   */
  _startAnalysisLoop() {
    const updateAnalysis = () => {
      if (!this.isInitialized || !this.nodes.analyser) return;
      
      const analyser = this.nodes.analyser;
      const bufferLength = analyser.frequencyBinCount;
      const timeData = new Float32Array(bufferLength);
      const freqData = new Float32Array(bufferLength);
      
      analyser.getFloatTimeDomainData(timeData);
      analyser.getFloatFrequencyData(freqData);
      
      // Calculate RMS
      let sum = 0;
      let peak = 0;
      for (let i = 0; i < bufferLength; i++) {
        sum += timeData[i] * timeData[i];
        peak = Math.max(peak, Math.abs(timeData[i]));
      }
      this.analysis.rms = Math.sqrt(sum / bufferLength);
      this.analysis.peak = peak;
      
      // Calculate spectral centroid
      let weightedSum = 0;
      let totalEnergy = 0;
      const nyquist = this.audioContext.sampleRate / 2;
      for (let i = 0; i < bufferLength; i++) {
        const magnitude = Math.pow(10, freqData[i] / 20);
        const frequency = (i / bufferLength) * nyquist;
        weightedSum += magnitude * frequency;
        totalEnergy += magnitude;
      }
      this.analysis.spectralCentroid = totalEnergy > 0 ? weightedSum / totalEnergy : 0;
      
      // Estimate limiter gain reduction
      this.analysis.limiterGR = this.nodes.masterLimiter.reduction || 0;
      
      // Update pulse phase
      if (this.params.pulse.enabled) {
        const t = this.audioContext.currentTime;
        this.params.pulse.phase = (t * this.params.pulse.freq_hz) % 1;
      }
      
      // Call update callback
      if (this.onAnalysisUpdate) {
        this.onAnalysisUpdate({
          rms: this.analysis.rms,
          rms_db: gainToDb(this.analysis.rms),
          peak: this.analysis.peak,
          peak_db: gainToDb(this.analysis.peak),
          spectralCentroid: this.analysis.spectralCentroid,
          limiterGR: this.analysis.limiterGR,
          tempo: this.analysis.tempo,
          pulsePhase: this.params.pulse.phase
        });
      }
      
      requestAnimationFrame(updateAnalysis);
    };
    
    updateAnalysis();
  }
  
  /**
   * Connect audio input source
   */
  connectSource(sourceNode) {
    if (!this.isInitialized) return;
    sourceNode.connect(this.nodes.inputGain);
    this.isProcessing = true;
  }
  
  /**
   * Connect microphone input
   */
  async connectMicrophone() {
    if (!this.isInitialized) await this.init();
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.nodes.micSource = this.audioContext.createMediaStreamSource(stream);
      this.nodes.micSource.connect(this.nodes.inputGain);
      this.isProcessing = true;
      return true;
    } catch (err) {
      console.error("[EntrainmentEngine] Mic access failed:", err);
      return false;
    }
  }
  
  /**
   * Disconnect microphone
   */
  disconnectMicrophone() {
    if (this.nodes.micSource) {
      this.nodes.micSource.disconnect();
      this.nodes.micSource = null;
    }
    this.isProcessing = false;
  }
  
  /**
   * Apply a preset mode
   */
  applyMode(modeName) {
    const preset = PRESETS[modeName];
    if (!preset) return false;
    
    this.currentMode = modeName;
    
    // Apply preset parameters
    this.setPulseParams(preset.pulse);
    this.setBinauralParams(preset.binaural);
    this.setEmbedParams(preset.embed);
    
    if (this.onStateChange) {
      this.onStateChange({ mode: modeName, preset });
    }
    
    return true;
  }
  
  /**
   * Set pulse modulator parameters
   */
  setPulseParams(params) {
    Object.assign(this.params.pulse, params);
    
    if (!this.isInitialized) return;
    
    const ctx = this.audioContext;
    const now = ctx.currentTime;
    const smoothTime = SAFETY.smoothingTime_ms / 1000;
    
    // Clamp depth to safety limit
    const depth = Math.min(params.depth || this.params.pulse.depth, SAFETY.maxPulseDepth);
    
    this.nodes.pulseLFO.frequency.setTargetAtTime(
      params.freq_hz || this.params.pulse.freq_hz, now, smoothTime
    );
    this.nodes.pulseLFOGain.gain.setTargetAtTime(
      params.enabled !== false ? depth : 0, now, smoothTime
    );
  }
  
  /**
   * Set binaural parameters
   */
  setBinauralParams(params) {
    Object.assign(this.params.binaural, params);
    
    if (!this.isInitialized) return;
    
    const ctx = this.audioContext;
    const now = ctx.currentTime;
    const smoothTime = SAFETY.smoothingTime_ms / 1000;
    
    const carrier = params.carrier_hz || this.params.binaural.carrier_hz;
    const beat = params.beat_hz || this.params.binaural.beat_hz;
    
    // Clamp level to safety limit
    const levelDb = Math.max(params.level_db || this.params.binaural.level_db, SAFETY.maxBinauralLevel_db);
    const level = dbToGain(levelDb);
    
    this.nodes.binauralLeft.frequency.setTargetAtTime(carrier - beat / 2, now, smoothTime);
    this.nodes.binauralRight.frequency.setTargetAtTime(carrier + beat / 2, now, smoothTime);
    
    const enabled = params.enabled && this.params.headphoneMode;
    this.nodes.binauralGainL.gain.setTargetAtTime(enabled ? level : 0, now, smoothTime);
    this.nodes.binauralGainR.gain.setTargetAtTime(enabled ? level : 0, now, smoothTime);
  }
  
  /**
   * Set spectral embed parameters
   */
  setEmbedParams(params) {
    Object.assign(this.params.embed, params);
    
    if (!this.isInitialized) return;
    
    const ctx = this.audioContext;
    const now = ctx.currentTime;
    const smoothTime = SAFETY.smoothingTime_ms / 1000;
    
    const amount = params.enabled !== false ? (params.amount || this.params.embed.amount) : 0;
    this.nodes.embedGain.gain.setTargetAtTime(amount, now, smoothTime);
    
    // Update filter bands
    if (params.bands) {
      params.bands.forEach((band, i) => {
        if (this.nodes.embedFilters[i]) {
          const centerFreq = Math.sqrt(band.low_hz * band.high_hz);
          this.nodes.embedFilters[i].frequency.setTargetAtTime(centerFreq, now, smoothTime);
          this.nodes.embedFilters[i].Q.setTargetAtTime(band.q, now, smoothTime);
        }
      });
    }
  }
  
  /**
   * Set headphone mode (enables binaural)
   */
  setHeadphoneMode(enabled) {
    this.params.headphoneMode = enabled;
    this.setBinauralParams(this.params.binaural);
  }
  
  /**
   * Set input trim
   */
  setInputTrim(db) {
    this.params.inputTrim_db = db;
    if (this.isInitialized) {
      this.nodes.inputGain.gain.setTargetAtTime(
        dbToGain(db), this.audioContext.currentTime, SAFETY.smoothingTime_ms / 1000
      );
    }
  }
  
  /**
   * Set output trim
   */
  setOutputTrim(db) {
    this.params.outputTrim_db = db;
    if (this.isInitialized) {
      this.nodes.outputGain.gain.setTargetAtTime(
        dbToGain(db), this.audioContext.currentTime, SAFETY.smoothingTime_ms / 1000
      );
    }
  }
  
  /**
   * Set intensity (scales all depths/amounts proportionally)
   */
  setIntensity(value) {
    // value 0-1, maps to safe scaling
    const scale = Math.max(0, Math.min(1, value));
    
    this.setPulseParams({ depth: this.params.pulse.depth * scale });
    this.setEmbedParams({ amount: this.params.embed.amount * scale });
    
    // Binaural level scales in dB
    const binauralScale = -36 + (scale * 18); // -36 to -18 dB
    this.setBinauralParams({ level_db: binauralScale });
  }
  
  /**
   * Export current state as JSON preset
   */
  exportPreset() {
    return {
      version: 1,
      meta: {
        name: `Custom_${Date.now()}`,
        author: "GODMODE User",
        created_utc: new Date().toISOString()
      },
      global: {
        input_trim_db: this.params.inputTrim_db,
        output_trim_db: this.params.outputTrim_db,
        max_output_ceiling_db: SAFETY.maxOutputCeiling_db,
        hq_mode: this.params.hqMode,
        headphone_mode: this.params.headphoneMode
      },
      pulse: { ...this.params.pulse },
      binaural: { ...this.params.binaural },
      embed: { ...this.params.embed },
      io: { osc: { ...this.params.osc } }
    };
  }
  
  /**
   * Import preset from JSON
   */
  importPreset(preset) {
    if (preset.global) {
      this.setInputTrim(preset.global.input_trim_db || 0);
      this.setOutputTrim(preset.global.output_trim_db || 0);
      this.params.headphoneMode = preset.global.headphone_mode || false;
    }
    if (preset.pulse) this.setPulseParams(preset.pulse);
    if (preset.binaural) this.setBinauralParams(preset.binaural);
    if (preset.embed) this.setEmbedParams(preset.embed);
    
    return true;
  }
  
  /**
   * Get OSC message data (for external integration)
   */
  getOscData() {
    return {
      "/gm/state": {
        rms: this.analysis.rms,
        rms_db: gainToDb(this.analysis.rms),
        bpm: this.analysis.tempo,
        limiter_gr_db: this.analysis.limiterGR
      },
      "/gm/pulse": {
        freq_hz: this.params.pulse.freq_hz,
        depth: this.params.pulse.depth,
        phase: this.params.pulse.phase
      },
      "/gm/binaural": {
        carrier_hz: this.params.binaural.carrier_hz,
        beat_hz: this.params.binaural.beat_hz,
        level_db: this.params.binaural.level_db
      },
      "/gm/embed": {
        amount: this.params.embed.amount,
        duck_strength: this.params.embed.duck_strength
      }
    };
  }
  
  /**
   * Suspend audio processing
   */
  suspend() {
    if (this.audioContext) {
      this.audioContext.suspend();
    }
  }
  
  /**
   * Resume audio processing
   */
  resume() {
    if (this.audioContext) {
      this.audioContext.resume();
    }
  }
  
  /**
   * Cleanup and destroy engine
   */
  destroy() {
    this.disconnectMicrophone();
    
    if (this.nodes.pulseLFO) this.nodes.pulseLFO.stop();
    if (this.nodes.binauralLeft) this.nodes.binauralLeft.stop();
    if (this.nodes.binauralRight) this.nodes.binauralRight.stop();
    if (this.nodes.noiseSource) this.nodes.noiseSource.stop();
    
    if (this.audioContext) {
      this.audioContext.close();
    }
    
    this.isInitialized = false;
    this.isProcessing = false;
  }
}

// Singleton instance
let engineInstance = null;

export const getEntrainmentEngine = () => {
  if (!engineInstance) {
    engineInstance = new EntrainmentEngine();
  }
  return engineInstance;
};

export default EntrainmentEngine;
